import React, { useState, useEffect, useCallback } from 'react';
import { z } from 'zod';
import { AlertTriangle, Play, Search } from 'lucide-react';
import { CodeEditor } from '../CodeEditor/CodeEditor';
import { LabelWithIcon } from '../ui/LabelWithIcon';
import { useSchemaMapperStore } from '../../store/schemaMapperStore';
import { useExecuteFunction } from '../../api/execApi';
import { SchemaField, ValidationResult } from '../../types/schemaMapperTypes';
import { validateFieldValue } from './utils';

interface FieldOutputPreviewProps {
  field: SchemaField;
  sampleData: Record<string, unknown>;
  destinationZod: z.ZodTypeAny;
}

export const FieldOutputPreview: React.FC<FieldOutputPreviewProps> = React.memo(({ 
  field, 
  sampleData, 
  destinationZod 
}) => {
  // Use state to store the evaluated output
  const [outputResult, setOutputResult] = useState<{
    value: unknown;
    error: string | null;
  }>({ value: undefined, error: null });
  
  // Add a flag to track if the function has been executed
  const [hasExecuted, setHasExecuted] = useState(false);
  
  // Add validation state - used for local UI rendering only
  const [validation, setValidation] = useState<ValidationResult>({ isValid: true });
  
  // Get the setFieldValidation from the store
  const setFieldValidation = useSchemaMapperStore(state => state.setFieldValidation);

  // Use tanstack query for function execution
  const executeFunction = useExecuteFunction();

  // Function to execute the function via API
  const handleExecuteFunction = useCallback(async () => {
    if (!field.name || field.type !== 'function') return;
    
    try {
      const functionToExecute = field.functionValue;
      
      // First, ensure the store is updated with the current function value
      // This ensures the function is saved before execution
      setFieldValidation(field.name, true); // Optimistically mark as valid
      
      console.log(`Sending function to API for execution: ${functionToExecute.substring(0, 50)}...`);
      
      // Call the mutation with function and input data
      const response = await executeFunction.mutateAsync({
        fn: functionToExecute,
        input: sampleData
      });
      
      // Update the output result with the response
      const result = response.result;
      setOutputResult({ value: result, error: null });
      setHasExecuted(true);
      
      // Validate the result against the schema if available
      if (destinationZod && field.name) {
        const validationResult = validateFieldValue(destinationZod, field.name, result);
        setValidation(validationResult);
        
        // Update the store with validation result
        setFieldValidation(field.name, validationResult.isValid);
      } else {
        // If no schema or field has no name, consider it valid
        setValidation({ isValid: true });
        if (field.name) {
          setFieldValidation(field.name, true);
        }
      }
    } catch (error: unknown) {
      console.error('API execution failed:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : (error as { message?: string })?.message || 'Unknown error';
      setOutputResult({ value: undefined, error: errorMessage });
      setHasExecuted(true);
      
      // Update validation - if we have an error, the field is invalid
      setValidation({ isValid: false });
      if (field.name) {
        setFieldValidation(field.name, false);
      }
    }
  }, [field, sampleData, destinationZod, setFieldValidation, executeFunction]);

  // Handler for path-based fields - this still evaluates locally and doesn't need API
  const evaluatePathField = useCallback(() => {
    if (!field.name || field.type !== 'path') return;
    
    try {
      // Handle dot notation path
      const path = field.value.split('.');
      let value: unknown = { ...sampleData };

      for (const segment of path) {
        if (
          value &&
          typeof value === 'object' &&
          segment in (value as Record<string, unknown>)
        ) {
          value = (value as Record<string, unknown>)[segment];
        } else {
          setOutputResult({
            value: undefined,
            error: `Path segment '${segment}' not found in sample data`,
          });
          
          // Update validation - if we have an error, the field is invalid
          setValidation({ isValid: false });
          if (field.name) {
            setFieldValidation(field.name, false);
          }
          return;
        }
      }

      setOutputResult({ value, error: null });
      
      // Validate the result against the schema if available
      if (destinationZod && field.name) {
        const validationResult = validateFieldValue(destinationZod, field.name, value);
        setValidation(validationResult);
        
        // Update the store with validation result
        setFieldValidation(field.name, validationResult.isValid);
      } else {
        // If no schema or field has no name, consider it valid
        setValidation({ isValid: true });
        if (field.name) {
          setFieldValidation(field.name, true);
        }
      }
    } catch (e) {
      console.error('Path evaluation failed:', e);
      setOutputResult({ value: undefined, error: (e as Error).message });
      
      // Update validation - if we have an error, the field is invalid
      setValidation({ isValid: false });
      if (field.name) {
        setFieldValidation(field.name, false);
      }
    }
  }, [field, sampleData, destinationZod, setFieldValidation]);

  // Auto-evaluate path fields when they change
  useEffect(() => {
    if (field.type === 'path') {
      evaluatePathField();
    }
  }, [field, evaluatePathField]);

  // Helper function to format the output value for display
  const formatOutputValue = (value: unknown): string => {
    if (value === undefined) {
      return 'undefined';
    }

    if (value === null) {
      return 'null';
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return `[Object] (Cannot stringify: ${(e as Error).message})`;
      }
    }

    if (typeof value === 'function') {
      return '[Function]';
    }

    return String(value);
  };

  const { value, error } = outputResult;
  const formattedValue = formatOutputValue(value);
  const isMultiLine = formattedValue.includes('\n');

  return (
    <div className="field-output-preview">
      <div className="output-preview-header">
        <LabelWithIcon icon={<Search />} text="Live Output Preview" />
        {field.type === 'function' && (
          <button
            className={`button-secondary ${executeFunction.isPending ? 'disabled' : ''}`}
            onClick={handleExecuteFunction}
            disabled={executeFunction.isPending}
            title="Save and execute function via API"
          >
            {executeFunction.isPending ? 'Running...' : <><Play size={16} /> Save & Execute</>}
          </button>
        )}
      </div>
      <div className="output-preview-content">
        {executeFunction.isPending ? (
          <div className="output-function-preview-loading">Executing function...</div>
        ) : error ? (
          <div className="output-preview-error">
            {error}
            {field.type === 'function' && (
              <div className="error-help-text">
                Try editing the function and click <Play size={16} /> Save & Execute to update the preview
              </div>
            )}
          </div>
        ) : field.type === 'function' && !hasExecuted ? (
          <div className="output-preview-pending">
            <LabelWithIcon 
              icon={<Play />} 
              text="Click Save & Execute to see output" 
              className="pending-message" 
            />
          </div>
        ) : isMultiLine ? (
          <div className="output-preview-multiline">
            <CodeEditor
              value={formattedValue}
              height="100px"
              readOnly={true}
            />
            {!validation.isValid && (
              <div className="validation-error">
                <LabelWithIcon icon={<AlertTriangle />} text={validation.errorMessage || ""} className="validation-message" />
              </div>
            )}
          </div>
        ) : (
          <div className="output-preview-single-line">
            {formattedValue}
            {!validation.isValid && (
              <div className="validation-error">
                <LabelWithIcon icon={<AlertTriangle />} text={validation.errorMessage || ""} className="validation-message" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}); 